import { inject, Injectable } from '@angular/core';
import { SubsectionPreviewComponent } from '../../../project-subsection/preview/subsection-preview.component';
import { SubsectionService } from '../../services/subsection.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export interface TopMediaInfoState {
  isFullscreen: boolean;
  isShareLinkPreview: boolean;
  isReleasePreview: boolean;
  isShareLinksEnabled: boolean;
  isCommentVisible: boolean;
  isAssetInfoVisible: boolean;
  isAssetListVisible: boolean;
  isVersionListVisible: boolean;
  isVotingVisible: boolean;
  isCompareVisible: boolean;
  isCommentCollapsed: boolean;
  isExplorerCollapsed: boolean;
  isWidgetCollapsed: boolean;
  isListViewMode: boolean;
  showFullTopSubSectionNav: boolean;
  showNavigationButtons: boolean;
  showListViewModeButton: boolean;
  subsectionPreview: SubsectionPreviewComponent;
  isOnProjectSubsection: boolean;
  isKpfWidgetExpanded: boolean;
  isPdfSliderCollapsed: boolean;
}

const initialState: TopMediaInfoState = {
  isFullscreen: false,
  isShareLinkPreview: false,
  isReleasePreview: false,
  isShareLinksEnabled: false,
  isCommentVisible: true,
  isAssetInfoVisible: true,
  isAssetListVisible: true,
  isVersionListVisible: true,
  isVotingVisible: true,
  isCompareVisible: true,
  isCommentCollapsed: false,
  isExplorerCollapsed: false,
  isWidgetCollapsed: false,
  isListViewMode: false,
  showFullTopSubSectionNav: false,
  showNavigationButtons: true,
  showListViewModeButton: true,
  subsectionPreview: null,
  isOnProjectSubsection: false,
  isKpfWidgetExpanded: false,
  isPdfSliderCollapsed: false,
};

@Injectable({
  providedIn: 'root',
})
export class TopMediaInfoService {
  private readonly state = new BehaviorSubject<TopMediaInfoState>(initialState);
  private readonly subsectionService = inject(SubsectionService);
  private readonly toggleAssetInfo$ = new Subject<void>();
  private readonly downloadAsset$ = new Subject<void>();

  // State getters
  get isFullscreen(): boolean {
    return this.state.value.isFullscreen;
  }
  get isShareLinkPreview(): boolean {
    return this.state.value.isShareLinkPreview;
  }
  get isShareLinksEnabled(): boolean {
    return this.state.value.isShareLinksEnabled;
  }
  get isCommentVisible(): boolean {
    return this.state.value.isCommentVisible;
  }
  get isAssetInfoVisible(): boolean {
    return this.state.value.isAssetInfoVisible;
  }
  get isAssetListVisible(): boolean {
    return this.state.value.isAssetListVisible;
  }
  get isVersionListVisible(): boolean {
    return this.state.value.isVersionListVisible;
  }
  get isVotingVisible(): boolean {
    return this.state.value.isVotingVisible;
  }
  get isCompareVisible(): boolean {
    return this.state.value.isCompareVisible;
  }
  get isCommentCollapsed(): boolean {
    return this.state.value.isCommentCollapsed;
  }
  get isExplorerCollapsed(): boolean {
    return this.state.value.isExplorerCollapsed;
  }
  get isWidgetCollapsed(): boolean {
    return this.state.value.isWidgetCollapsed;
  }
  get isListViewMode(): boolean {
    return this.state.value.isListViewMode;
  }
  get showFullTopSubSectionNav(): boolean {
    return this.state.value.showFullTopSubSectionNav;
  }
  get showNavigationButtons(): boolean {
    return this.state.value.showNavigationButtons;
  }
  get showListViewModeButton(): boolean {
    return this.state.value.showListViewModeButton;
  }

  get isOnProjectSubsection(): boolean {
    return this.state.value.isOnProjectSubsection;
  }

  get isKpfWidgetExpanded(): boolean {
    return this.state.value.isKpfWidgetExpanded;
  }

  get isPdfSliderCollapsed(): boolean {
    return this.state.value.isPdfSliderCollapsed;
  }

  // State updates
  updateState(partialState: Partial<TopMediaInfoState>): void {
    const newState = {
      ...this.state.value,
      ...partialState,
    };
    this.state.next(newState);
  }

  state$(): Observable<TopMediaInfoState> {
    return this.state.asObservable();
  }

  toggleFullscreen(): void {
    const _isFullscreen = !this.state.value.isFullscreen;
    this.state.next({
      ...this.state.value,
      isFullscreen: _isFullscreen,
    });
  }

  toggleAssetInfo(): void {
    this.state.next({
      ...this.state.value,
      isAssetInfoVisible: !this.state.value.isAssetInfoVisible,
    });
  }

  toggleAssetList(): void {
    this.state.next({
      ...this.state.value,
      isAssetListVisible: !this.state.value.isAssetListVisible,
    });
  }

  toggleVersionList(): void {
    this.state.next({
      ...this.state.value,
      isVersionListVisible: !this.state.value.isVersionListVisible,
    });
  }

  toggleCommentCollapsed(): void {
    this.state.next({
      ...this.state.value,
      isCommentCollapsed: !this.state.value.isCommentCollapsed,
    });
  }

  toggleExplorerCollapsed(): void {
    this.state.next({
      ...this.state.value,
      isExplorerCollapsed: !this.state.value.isExplorerCollapsed,
    });
  }

  toggleWidgetCollapsed(): void {
    this.state.next({
      ...this.state.value,
      isWidgetCollapsed: !this.state.value.isWidgetCollapsed,
    });
  }

  toggleListViewMode(): void {
    this.state.next({
      ...this.state.value,
      isListViewMode: !this.state.value.isListViewMode,
    });
  }

  togglePdfSliderCollapsed(): void {
    this.state.next({
      ...this.state.value,
      isPdfSliderCollapsed: !this.state.value.isPdfSliderCollapsed,
    });
  }

  updatePdfSliderCollapsed(collapsed: boolean): void {
    this.state.next({
      ...this.state.value,
      isPdfSliderCollapsed: collapsed,
    });
  }

  resetState(): void {
    this.state.next(initialState);
  }

  getSubsectionPreview(): SubsectionPreviewComponent | null {
    return this.state.value.subsectionPreview;
  }

  emitToggleAssetInfo(): void {
    this.toggleAssetInfo$.next();
  }

  onToggleAssetInfo(): Observable<void> {
    return this.toggleAssetInfo$.asObservable();
  }

  emitDownloadAsset(): void {
    this.downloadAsset$.next();
  }

  onDownloadAsset(): Observable<void> {
    return this.downloadAsset$.asObservable();
  }
}
